import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { X, ChevronRight, ChevronDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { CategoryWithRelations } from "@/features/categories/types";

interface HierarchicalCategorySelectProps {
  categories: CategoryWithRelations[];
  value: number[];
  onValueChange: (value: number[]) => void;
  placeholder?: string;
}

export function HierarchicalCategorySelect({
  categories,
  value,
  onValueChange,
  placeholder = "Select categories..."
}: HierarchicalCategorySelectProps) {
  const [open, setOpen] = React.useState(false);
  const [expandedCategories, setExpandedCategories] = React.useState<Set<number>>(new Set());

  // Get parent categories (categories without parent_id)
  const parentCategories = React.useMemo(() => {
    return categories.filter(cat => !cat.parent_id);
  }, [categories]);

  // Build a map of selected categories for quick lookup
  const selectedCategoriesMap = React.useMemo(() => {
    return categories.filter(cat => value.includes(cat.id));
  }, [categories, value]);

  // Toggle expand/collapse for parent categories
  const toggleExpand = (categoryId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Handle category selection with auto-parent selection
  const handleSelect = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;

    let newValue: number[];

    if (value.includes(categoryId)) {
      // Deselecting - remove this category and all its children
      const childIds = category.children?.map(c => c.id) || [];
      newValue = value.filter(id => id !== categoryId && !childIds.includes(id));
    } else {
      // Selecting - add this category
      newValue = [...value, categoryId];

      // If this is a child category, also select the parent
      if (category.parent_id && !value.includes(category.parent_id)) {
        newValue.push(category.parent_id);
      }
    }

    onValueChange(newValue);
  };

  // Render category item with proper indentation
  const renderCategoryItem = (category: CategoryWithRelations, isChild: boolean = false) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const isSelected = value.includes(category.id);

    return (
      <React.Fragment key={category.id}>
        <CommandItem
          value={category.name}
          onSelect={() => handleSelect(category.id)}
          className={cn(
            "flex items-center gap-2",
            isChild && "pl-8",
            isSelected && "bg-accent"
          )}
        >
          {!isChild && hasChildren && (
            <button
              onClick={(e) => toggleExpand(category.id, e)}
              className="flex-shrink-0"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
          {!isChild && !hasChildren && (
            <div className="w-4" />
          )}
          {isChild && (
            <span className="text-muted-foreground mr-2">↳</span>
          )}
          <span className="flex-1">{category.name}</span>
          {isSelected && (
            <span className="text-xs text-muted-foreground">✓</span>
          )}
        </CommandItem>

        {/* Render children if expanded */}
        {hasChildren && isExpanded && category.children?.map(child =>
          renderCategoryItem(child, true)
        )}
      </React.Fragment>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <div className="relative flex min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer">
          <div className="flex flex-wrap gap-1">
            {selectedCategoriesMap.map((category) => (
              <Badge
                key={category.id}
                variant="secondary"
                className="flex items-center gap-1"
              >
                {category.parent_name ? `${category.parent_name} → ${category.name}` : category.name}
                <button
                  className="rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSelect(category.id);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(category.id);
                  }}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {selectedCategoriesMap.length === 0 && (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search categories..." />
          <CommandList>
            <CommandEmpty>No categories found.</CommandEmpty>
            <CommandGroup>
              {parentCategories.map(category => renderCategoryItem(category))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
